import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Tooltip, TextField, Pagination, useMediaQuery, useTheme, Button, SelectChangeEvent, Stack, InputAdornment } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchIcon from '@mui/icons-material/Search';
import DefaultProfile from '../../../Assets/DefaultProfile.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStyles } from './styles';
import routes from '../../../Utils/routes';
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

const ITEMS_PER_PAGE = 20;

const MentorshipMobileview: React.FC<any> = ({ cardData, type, showAll, title }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [paginatedData, setPaginatedData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedValues, setSelectedValues] = useState<any>({
    locations: [],
    graduationYear: '',
    currentCompany: '',
    postCompany: '',
    industry: '',
  });
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState(''); // e.g. 'A-Z', 'Z-A', etc.
  const [open, setOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({
    locations: [],
    graduationYear: '',
    currentCompany: '',
    postCompany: '',
    industry: '',
  });
  const sourceData = state?.data?.length ? state?.data : cardData || [];
  const filteredData = useMemo(() => {
    const getNestedFieldValue = (job: any, key: string): any => {
      const pathMap: Record<string, string> = {
        locations: 'contactDetails.city',
        graduationYear: 'contactDetails.batch',
        currentCompany: 'contactDetails.company',
        postCompany: 'contactDetails.postCompany',
        industry: 'contactDetails.industry',
      };
      const path = pathMap[key] || key;
      return path.split('.').reduce((obj, part) => obj?.[part], job);
    };

    const matchesCriteria = (job: any) => {
      return Object.entries(appliedFilters).every(([key, selected]) => {
        if (!selected || (Array.isArray(selected) && selected.length === 0)) return true;
        const jobValue = getNestedFieldValue(job, key);
        return Array.isArray(selected) ? selected.includes(jobValue) : jobValue === selected;
      });
    };

    const matchesSearch = (job: any) => {
      const fields = [
        'contactDetails.company',
        'contactDetails.designation',
        'contactDetails.industry',
        'contactDetails.interests',
        'contactDetails.name',
        'contactDetails.occupation',
        'contactDetails.postCompany',
        'contactDetails.program',
        'contactDetails.street',
        'contactDetails.batch',
      ];
      return fields.some((field) => {
        const value = field.split('.').reduce((obj, part) => obj?.[part], job);
        return value?.toString().toLowerCase().includes(searchQuery.toLowerCase());
      });
    };

    return sourceData.filter((job: any) => matchesCriteria(job) && matchesSearch(job));
  }, [sourceData, appliedFilters, searchQuery]);
  useEffect(() => {
    let dataToPaginate: any[] = [];

    if (!showAll && !state && cardData) {
      dataToPaginate = filteredData.slice(0, 2);
      setTotalPages(1);
    } else if (isMentorshipRoute) {
      dataToPaginate = filteredData;
      setTotalPages(1);
    } else {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      dataToPaginate = filteredData.slice(startIndex, endIndex);
      setTotalPages(Math.ceil(filteredData.length / ITEMS_PER_PAGE));
    }

    setPaginatedData(dataToPaginate);
  }, [filteredData, currentPage, showAll, state, cardData]);
  useEffect(() => {
    if (filteredData.length === 0 && (searchQuery || Object.values(appliedFilters).some((v) => v))) {
      setAppliedFilters({
        locations: [],
        graduationYear: '',
        currentCompany: '',
        postCompany: '',
        industry: '',
      });
    }
  }, [filteredData]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleApply = () => {
    // Set applied filters and reset to page 1
    setAppliedFilters(selectedValues);
    setCurrentPage(1); // Reset to first page
    setOpen(false); // Close the filter dialog
  };
  const handleFilterChange = (filterName: string, value: any) => {
    setSelectedValues((prevValues: any) => ({
      ...prevValues,
      [filterName]: value,
    }));
  };

  const locationOptions: string[] = useMemo(() => {
    return Array.from(new Set(state?.data?.map((user: any) => user?.contactDetails?.city))).filter(Boolean) as string[];
  }, [state?.data]);

  const graduationYearOptions: string[] = useMemo(() => {
    return Array.from(new Set(state?.data?.map((user: any) => user?.contactDetails?.batch))).filter(Boolean) as string[];
  }, [state?.data]);

  const currentCompanyOptions: string[] = useMemo(() => {
    return Array.from(new Set(state?.data?.map((user: any) => user?.contactDetails?.company))).filter(Boolean) as string[];
  }, [state?.data]);

  const postCompanyOptions: string[] = useMemo(() => {
    return Array.from(new Set(state?.data?.map((user: any) => user?.contactDetails?.postCompany))).filter(Boolean) as string[];
  }, [state?.data]);

  const industryOptions: string[] = useMemo(() => {
    return Array.from(new Set(state?.data?.map((user: any) => user?.contactDetails?.industry))).filter(Boolean) as string[];
  }, [state?.data]);
  const handleSort = (option: string) => {
    let sortedData = [...filteredData];

    switch (option) {
      case 'A-Z':
        sortedData.sort((a, b) => a?.contactDetails?.name?.localeCompare(b?.contactDetails?.name));
        break;
      case 'Z-A':
        sortedData.sort((a, b) => b?.contactDetails?.name?.localeCompare(a?.contactDetails?.name));
        break;
      case 'Recently Joined':
        sortedData.sort((a, b) => new Date(b?.contactDetails?.joinedDate).getTime() - new Date(a?.contactDetails?.joinedDate).getTime());
        break;
      case 'Clear':
        sortedData = [...sourceData];
        break;
      default:
        break;
    }

    setCurrentPage(1);
    setPaginatedData(isMentorshipRoute ? sortedData : sortedData.slice(0, ITEMS_PER_PAGE));
  };
  const isMentorshipRoute = location.pathname === '/mentorship/view-all-mentorship';

  return (
    <div className={classes.networkCardContainermobile} style={{ marginBottom: isMentorshipRoute ? 80 : 24 }}>
      <Box width="100%">
        <Paper className={classes.cardHolder}>
          {paginatedData.length === 0 ? (
            <>
              {isMentorshipRoute && (
                <Box display="flex" justifyContent="flex-end" mb={2} padding={2}>
                  <TextField
                    variant="outlined"
                    placeholder="Search by name or designation"
                    size="small"
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1); // Reset to page 1 on new search
                    }}
                    value={searchQuery}
                    InputProps={{
                      style: { borderRadius: '8px', backgroundColor: '#fff' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#8C8E94', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: isMobile ? '100%' : '260px',
                      mr: isMobile ? 0 : 2,
                    }}
                  />
                </Box>
              )}
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} height={'50vh'}>
                <Typography variant="body2" color="textSecondary">
                  You have not {type} yet
                </Typography>
                <Typography variant="h5" textAlign={'center'} color="textSecondary" gutterBottom>
                  Get started by connecting with people in your network
                </Typography>
              </Box>
              {isMentorshipRoute && (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    backgroundColor: 'transparent',
                    padding: 2,
                    zIndex: 1000,
                  }}
                >
                  <Grid item xs={10}></Grid>
                  <Grid item xs={2}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1300,
                        width: '48px',
                        height: '48px',
                        minWidth: 'auto',
                        borderRadius: '50%',
                        padding: 0,
                        backgroundColor: '#2C58E8',
                        color: '#fff',
                        boxShadow: 4,
                        '&:hover': {
                          backgroundColor: '#2449c2',
                        },
                      }}
                      onClick={handleOpen}
                    >
                      <FilterListIcon sx={{ fontSize: 24 }} />
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button size="small" variant="contained" color="info" fullWidth onClick={() => setSortDialogOpen(true)} className={classes.filterBtn}>
                      Sort by
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={() => {
                        navigate(routes.setupPreference);
                      }}
                    >
                      Edit Preference
                    </Button>
                  </Grid>
                </Grid>
              )}
            </>
          ) : (
            <div>
              <div>
                <Stack direction="row" justifyContent="space-between" alignItems="center" px={2} pt={2}>
                  <Typography style={{ textAlign: 'left', padding: '10px', fontSize: '16px', fontWeight: 500 }}>{state?.title}</Typography>
                  {isMentorshipRoute && (
                    <Button variant="contained" color="secondary" size="small" onClick={handleOpen}>
                      Filter
                    </Button>
                  )}
                </Stack>

                {isMentorshipRoute && (
                  <Box display="flex" justifyContent="flex-end" mb={2} padding={2}>
                    <TextField
                      variant="outlined"
                      placeholder="Search by name or designation"
                      size="small"
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on new search
                      }}
                      value={searchQuery}
                      InputProps={{
                        style: { borderRadius: '8px', backgroundColor: '#fff' },
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#8C8E94', fontSize: 18 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: isMobile ? '100%' : '260px',
                        mr: isMobile ? 0 : 2,
                      }}
                    />
                  </Box>
                )}

                <Grid container>
                  {paginatedData.map((data: any, index: number) => (
                    <Grid item xs={6} sm={12} md={2.7} key={data.Id || index} style={{ padding: '6px' }}>
                      <div
                        className={classes.networkCard}
                        onClick={() => {
                          navigate(`${routes.mentorshipDetailview}/${data.contactDetails?.id}`, {
                            state: { data: cardData, breadCrumbs: data?.contactDetails?.name, title: title },
                          });
                        }}
                      >
                        {((!data?.isMentor && data?.mentorConnectionRequestSent) || (!data?.isMentee && data?.menteeConnectionRequestSent)) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              backgroundColor: '#6D6F74',
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 500,
                              px: 1.25,
                              py: 0.5,
                              borderBottomLeftRadius: '8px',
                              borderTopRightRadius: '8px',
                              zIndex : 99
                            }}
                          >
                            Request sent
                          </Box>
                        )}
                        <Grid spacing={1} alignContent="center" alignItems="center" display="flex" flexDirection="column">
                          <Box position="relative" width={80} height={80} mb={0.5}>
                            <img
                              src={data.contactDetails?.profilePicURL ? data.contactDetails?.profilePicURL : DefaultProfile}
                              alt="featured img"
                              className={classes.cardImage}
                              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                            />
                            {data?.contactDetails?.isOnline && <Box position="absolute" top={62} right={5} width={12} height={12} borderRadius="50%" bgcolor="#4CAF50" border="2px solid white" />}
                          </Box>
                          <Grid style={{ marginTop: '4px', textAlign: 'center' }}>
                            <Tooltip title={data?.contactDetails?.name || ''}>
                              <Typography className={classes.cardHeader}>
                                {data?.contactDetails?.name?.length > 15 ? `${data?.contactDetails?.name.slice(0, 15)}...` : data?.contactDetails?.name}
                              </Typography>
                            </Tooltip>
                            <Typography className={classes.cardActionContainerTypo}>
                              {data?.contactDetails?.designation ? data?.contactDetails?.designation : data?.contactDetails?.employmentDetails?.[0]?.Title__c || '-'}
                            </Typography>
                            <Typography
                              style={{
                                alignItems: 'center',
                                margin: '8px 0 0',
                                fontSize: '12px',
                                color: '#585A64',
                              }}
                            >
                              <LocationOnOutlinedIcon style={{ fontSize: 18, marginRight: 4 }} />
                              {data?.contactDetails?.country ? data?.contactDetails?.country : 'India'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                  ))}
                  {isMentorshipRoute && (
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: 'transparent',
                        padding: 2,
                        zIndex: 1000,
                      }}
                    >
                      <Grid item xs={10}></Grid>
                      <Grid item xs={2}></Grid>
                      <Grid item xs={6}>
                        <Button size="small" variant="contained" color="info" fullWidth onClick={() => setSortDialogOpen(true)} className={classes.filterBtn}>
                          Sort by
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          variant="contained"
                          style={{ color: '#5B4200' }}
                          fullWidth
                          onClick={() => {
                            navigate(routes.setupPreference);
                          }}
                        >
                          Edit Preference
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                  <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ fontSize: '18px', fontWeight: '500' }}>Filter</DialogTitle>
                    <DialogContent dividers>
                      <Grid container spacing={2} padding={2}>
                        {/* Location */}
                        <InputLabel id="location-label" style={{ fontSize: 14, marginTop: 6 }}>
                          Location
                        </InputLabel>
                        <FormControl fullWidth size="small" margin="normal">
                          <Select
                            labelId="location-label"
                            multiple
                            value={selectedValues.locations}
                            onChange={(e) => handleFilterChange('locations', e.target.value)}
                            renderValue={(selected) => selected.join(', ')}
                          >
                            {locationOptions.map((city) => (
                              <MenuItem key={city as string} value={city as string}>
                                {city}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Year of Graduation */}
                        <InputLabel id="year-label" style={{ fontSize: 14, marginTop: 6 }}>
                          Year of Graduation
                        </InputLabel>
                        <FormControl fullWidth size="small" margin="normal">
                          <Select labelId="year-label" value={selectedValues.graduationYear} onChange={(e) => handleFilterChange('graduationYear', e.target.value)}>
                            {graduationYearOptions.map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Current Company */}
                        <InputLabel id="company-label" style={{ fontSize: 14, marginTop: 6 }}>
                          Current Company
                        </InputLabel>
                        <FormControl fullWidth size="small" margin="normal">
                          <Select labelId="company-label" value={selectedValues.currentCompany} onChange={(e) => handleFilterChange('currentCompany', e.target.value)}>
                            {currentCompanyOptions.map((company) => (
                              <MenuItem key={company} value={company}>
                                {company}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Post Company */}
                        <InputLabel id="post-company-label" style={{ fontSize: 14, marginTop: 6 }}>
                          Post Company
                        </InputLabel>
                        <FormControl fullWidth size="small" margin="normal">
                          <Select labelId="post-company-label" value={selectedValues.postCompany} onChange={(e) => handleFilterChange('postCompany', e.target.value)}>
                            {postCompanyOptions.map((postCompany) => (
                              <MenuItem key={postCompany} value={postCompany}>
                                {postCompany}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Industry */}
                        <InputLabel id="industry-label" style={{ fontSize: 14, marginTop: 6 }}>
                          Industry
                        </InputLabel>
                        <FormControl fullWidth size="small" margin="normal">
                          <Select labelId="industry-label" value={selectedValues.industry} onChange={(e) => handleFilterChange('industry', e.target.value)}>
                            {industryOptions.map((industry) => (
                              <MenuItem key={industry} value={industry}>
                                {industry}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={handleClose}
                            sx={{
                              backgroundColor: '#E8EBF3',
                              maxWidth: '110px',
                              color: '#000',
                              '&:hover': {
                                backgroundColor: '#dce0ec',
                              },
                            }}
                            className={classes?.CancelBtn}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button variant="contained" fullWidth onClick={handleApply} style={{ color: '#5B4200' }}>
                            Apply
                          </Button>
                        </Grid>
                      </Grid>
                    </DialogActions>
                  </Dialog>
                  <Dialog open={sortDialogOpen} onClose={() => setSortDialogOpen(false)}>
                    <DialogTitle sx={{ fontSize: '18px', fontWeight: '500' }}>Sort Options</DialogTitle>
                    <DialogContent>
                      <List>
                        {['Clear', 'A-Z', 'Z-A', 'Recently Joined'].map((option) => (
                          <ListItem disablePadding key={option}>
                            <ListItemButton
                              onClick={() => {
                                setSortOption(option);
                                handleSort(option); // Apply your sort logic
                                setSortDialogOpen(false); // Close dialog
                              }}
                            >
                              <ListItemText primary={option} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </DialogContent>
                  </Dialog>
                </Grid>
              </div>
            </div>
          )}
        </Paper>
      </Box>
    </div>
  );
};

export default MentorshipMobileview;
